import {
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  useFloatingNodeId,
} from "@floating-ui/react";
import { type MouseEvent, useMemo } from "react";
import { cn, composeEventHandlers, Slot } from "../../utils";
import { DropdownProvider } from "./context";
import type { DropdownContextValue, DropdownProps } from "./types";
import { useViewModel } from "./useViewModel";

export const Dropdown = (props: DropdownProps) => {
  const {
    trigger,
    content,
    floatingFocusManager,
    contentClassName,
    ariaLabel,
    preventDefaultOnTrigger,
    stopPropagationOnTrigger,
    portal = true,
  } = props;

  const {
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    open,
    setOpen,
    dismiss,
    placement,
    activeIndex,
    setActiveIndex,
    navigationRef,
    isMounted,
    transitionStyles,
    triggerId,
    contentId,
  } = useViewModel(props);

  const nodeId = useFloatingNodeId();

  const contextValue = useMemo((): DropdownContextValue => {
    return {
      open,
      setOpen,
      placement,
      floatingStyles,
      refs,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
      activeIndex,
      setActiveIndex,
      navigationRef,
      context,
      isMounted,
      transitionStyles,
      dismiss,
      triggerId,
      contentId,
      props,
    };
  }, [
    open,
    setOpen,
    placement,
    floatingStyles,
    refs,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    activeIndex,
    setActiveIndex,
    navigationRef,
    context,
    isMounted,
    transitionStyles,
    dismiss,
    triggerId,
    contentId,
    props,
  ]);

  const { role } = props;
  const referenceProps = getReferenceProps();

  const preventEverything = (e: MouseEvent) => {
    if (stopPropagationOnTrigger) {
      e.stopPropagation();
    }
    if (preventDefaultOnTrigger) {
      e.preventDefault();
    }
  };

  const triggerElement = useMemo(() => {
    const { onClick: referenceOnClick, ...otherReferenceProps } =
      referenceProps;

    const composedOnClick = composeEventHandlers(
      composeEventHandlers(
        trigger.props.onClick,
        referenceOnClick as (e: MouseEvent) => void,
      ),
      preventEverything,
    );

    return (
      <Slot
        ref={refs.setReference}
        id={trigger.props.id || triggerId}
        tabIndex={trigger.props.tabIndex ?? 0}
        className={cn("outline-none", trigger.props.className)}
        onClick={composedOnClick}
        {...otherReferenceProps}
      >
        {trigger}
      </Slot>
    );
  }, [
    trigger,
    refs.setReference,
    triggerId,
    referenceProps,
    preventEverything,
  ]);

  const contentElement = useMemo(() => {
    if (!isMounted) return null;

    const floatingContent = (
      <div
        ref={refs.setFloating}
        style={{ ...floatingStyles, ...transitionStyles }}
        className={cn(
          "overflow-hidden rounded-lg bg-white shadow-md border border-neutral-500/5 text-sm outline-none z-[100]",
          contentClassName,
        )}
        aria-label={ariaLabel}
        aria-modal={floatingFocusManager?.modal ? "true" : undefined}
        {...getFloatingProps()}
        id={contentId}
        role={role}
      >
        {content}
      </div>
    );

    return (
      <FloatingFocusManager context={context} {...floatingFocusManager}>
        {floatingContent}
      </FloatingFocusManager>
    );
  }, [
    isMounted,
    transitionStyles,
    contentId,
    refs.setFloating,
    floatingStyles,
    contentClassName,
    ariaLabel,
    getFloatingProps,
    floatingFocusManager,
    context,
    content,
    role,
  ]);

  return (
    <DropdownProvider value={contextValue}>
      <FloatingNode id={nodeId}>
        <>{triggerElement}</>
        {portal ? (
          <FloatingPortal
            root={typeof portal === "boolean" ? undefined : portal}
          >
            {contentElement}
          </FloatingPortal>
        ) : (
          <>{contentElement}</>
        )}
      </FloatingNode>
    </DropdownProvider>
  );
};
